"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import { motion, AnimatePresence, useReducedMotion, type Variants } from "motion/react";
import { useRouter, usePathname } from "next/navigation";
import { ChevronLeft } from "lucide-react";
import { cn } from "@/util/cn";
import { usePortfolioOpen } from "@/hooks/modals/usePortfolioOpen";
import type { NavCategory, NavSubItem } from "./mobile-nav-config";
interface MobileNavSubnavProps {
  category: NavCategory | null;
  onBack: () => void;
}
export function MobileNavSubnav(t0) {
  const $ = _c(26);
  const {
    category,
    onBack
  } = t0;
  const router = useRouter();
  const pathname = usePathname();
  const prefersReducedMotion = useReducedMotion();
  const backButtonRef = useRef(null);
  const {
    onOpen: onPortfolioOpen
  } = usePortfolioOpen();
  let t1;
  let t2;
  if ($[0] !== category) {
    t1 = () => {
      if (category) {
        const timer = setTimeout(() => {
          backButtonRef.current?.focus();
        }, 100);
        return () => clearTimeout(timer);
      }
    };
    t2 = [category];
    $[0] = category;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== category || $[4] !== onBack) {
    t3 = () => {
      const handleKeyDown = e => {
        if (e.key === "Escape" && category) {
          onBack();
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    };
    t4 = [category, onBack];
    $[3] = category;
    $[4] = onBack;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] !== onBack || $[8] !== onPortfolioOpen || $[9] !== router) {
    t5 = item => {
      if (item.action) {
        if (item.action.type === "portfolio") {
          onPortfolioOpen(item.action.tab);
        } else {
          if (item.action.type === "external") {
            window.open(item.action.url, "_blank", "noopener,noreferrer");
          }
        }
      } else {
        if (item.href) {
          router.push(item.href);
        }
      }
      onBack();
    };
    $[7] = onBack;
    $[8] = onPortfolioOpen;
    $[9] = router;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleItemPress = t5;
  let t6;
  if ($[11] !== pathname) {
    t6 = href => {
      if (!href) {
        return false;
      }
      if (href === "/") {
        return pathname === "/";
      }
      return pathname === href || pathname.startsWith(href + "/");
    };
    $[11] = pathname;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const isItemActive = t6;
  let t7;
  if ($[13] !== prefersReducedMotion) {
    t7 = prefersReducedMotion ? {
      initial: {
        opacity: 0
      },
      animate: {
        opacity: 1
      },
      exit: {
        opacity: 0
      }
    } : {
      initial: {
        x: 15,
        opacity: 0
      },
      animate: {
        x: 0,
        opacity: 1,
        transition: {
          type: "spring",
          stiffness: 500,
          damping: 30,
          staggerChildren: 0.025
        }
      },
      exit: {
        opacity: 0,
        transition: {
          duration: 0.025
        }
      }
    };
    $[13] = prefersReducedMotion;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const containerVariants = t7;
  let t8;
  if ($[15] !== prefersReducedMotion) {
    t8 = prefersReducedMotion ? {
      initial: {
        opacity: 1
      },
      animate: {
        opacity: 1
      },
      exit: {
        opacity: 0
      }
    } : {
      initial: {
        x: 12,
        opacity: 0
      },
      animate: {
        x: 0,
        opacity: 1,
        transition: {
          type: "spring",
          stiffness: 500,
          damping: 25
        }
      },
      exit: {
        opacity: 0,
        transition: {
          duration: 0.01
        }
      }
    };
    $[15] = prefersReducedMotion;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const itemVariants = t8;
  let t9;
  if ($[17] !== category || $[18] !== containerVariants || $[19] !== handleItemPress || $[20] !== isItemActive || $[21] !== itemVariants || $[22] !== onBack) {
    t9 = category && <motion.div id={`subnav-${category.id}`} role="menu" aria-label={`${category.label} options`} className="absolute inset-0 flex items-center" variants={containerVariants} initial="initial" animate="animate" exit="exit"><span className="sr-only" aria-live="polite">{category.label} submenu, {category.subItems.length} options</span><motion.button ref={backButtonRef} variants={itemVariants} whileTap={{
        scale: 0.9
      }} onClick={onBack} className={cn("flex flex-col items-center justify-center px-3 py-1 min-h-[44px] min-w-[44px]", "text-default-500 hover:text-white transition-colors", "focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-xl")} aria-label="Back to main navigation"><div className="p-2 rounded-2xl"><ChevronLeft className="h-6 w-6" aria-hidden="true" /></div><span className="text-xs font-medium">Back</span></motion.button><div className="flex-1 flex items-center justify-around">{category.subItems.map(item_0 => {
          const Icon = item_0.icon;
          const active = isItemActive(item_0.href);
          return <motion.button key={item_0.href ?? item_0.label} role="menuitem" variants={itemVariants} whileTap={{
            scale: 0.9
          }} onClick={() => handleItemPress(item_0)} className={cn("flex flex-col items-center justify-center py-1 min-h-[44px] min-w-[44px] px-2", "focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-xl", "transition-colors")}><div className="relative">{active && <motion.div className="absolute inset-0 bg-secondary/20 rounded-full blur-md" initial={{
                scale: 0,
                opacity: 0
              }} animate={{
                scale: 1.2,
                opacity: 1
              }} transition={{
                ease: "anticipate",
                duration: 0.3
              }} aria-hidden="true" />}<div className={cn("p-2 rounded-2xl transition-colors relative", active ? "bg-secondary/10 text-secondary" : "text-default-600 hover:text-white")}><Icon className="h-5 w-5" aria-hidden="true" /></div></div><span className={cn("text-xs font-medium mt-1 transition-colors truncate max-w-[80px]", active ? "text-secondary" : "text-default-600")}>{item_0.label}</span></motion.button>;
        })}</div></motion.div>;
    $[17] = category;
    $[18] = containerVariants;
    $[19] = handleItemPress;
    $[20] = isItemActive;
    $[21] = itemVariants;
    $[22] = onBack;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t9) {
    t10 = <AnimatePresence mode="popLayout">{t9}</AnimatePresence>;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  return t10;
}